import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const LeadershipDevelopmentSection = () => {
  const developmentPath = [
    {
      level: "Prospect",
      duration: "0-6 months",
      skills: ["Community Service", "Team Collaboration", "Project Participation"],
      description: "Begin your journey with hands-on service projects and mentorship from experienced members while exploring club activities.",
      icon: "UserPlus",
      color: "bg-blue-500"
    },
    {
      level: "Active Member",
      duration: "After 6 months",
      skills: ["Project Leadership", "Public Speaking", "Event Planning"],
      description: "Official club member added to the portal, actively participating in projects and developing leadership skills through practice.",
      icon: "Target",
      color: "bg-green-500"
    },
    {
      level: "Director",
      duration: "Based on Interest & Contribution",
      skills: ["Strategic Planning", "Team Management", "Project Coordination"],
      description: "Lead specialized committees based on your interests and contributions. Every active member has a fair chance to become a director.",
      icon: "Users",
      color: "bg-orange-500"
    },
    {
      level: "Executive Board",
      duration: "Based on Leadership & Contribution",
      skills: ["Organizational Leadership", "Vision Setting", "Fellowship Building"],
      description: "Selected based on contribution as a director, project leadership, club wellbeing, fellowship, and demonstrated leadership qualities.",
      icon: "Crown",
      color: "bg-purple-500"
    }
  ];

  const benefits = [
    {
      category: "Professional Skills",
      items: ["Leadership & Management", "Public Speaking", "Project Management", "Financial Planning", "Strategic Thinking"]
    },
    {
      category: "Network Building",
      items: ["Lions International Network", "Community Leaders", "Corporate Partners", "Alumni Connections", "Peer Professionals"]
    },
    {
      category: "Career Advancement",
      items: ["Resume Enhancement", "Reference Letters", "Skill Certifications", "Leadership Experience", "Community Recognition"]
    }
  ];

  return (
    <section className="py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Leadership
            <span className="text-primary"> Development</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            At Colombo Circle, we believe every young person has the potential to lead. Through service, friendship, and shared experiences, we help you discover your strengths and grow into the leader you're meant to be.
          </p>
        </div>

        {/* Development Pathway */}
        <div className="mb-20">
          <h3 className="text-2xl font-bold text-foreground text-center mb-12">Your LEO Club Journey</h3>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {developmentPath.map((stage, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 h-full">
                  <div className="space-y-4">
                    <div className={`w-12 h-12 ${stage.color} rounded-xl flex items-center justify-center`}>
                      <Icon name={stage.icon} size={24} className="text-white" />
                    </div>

                    <div>
                      <h4 className="text-lg font-bold text-foreground">{stage.level}</h4>
                      <p className="text-sm text-muted-foreground">{stage.duration}</p>
                    </div>

                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {stage.description}
                    </p>

                    <div>
                      <h5 className="font-semibold text-foreground text-sm mb-2">Key Skills</h5>
                      <ul className="space-y-1">
                        {stage.skills.map((skill, skillIndex) => (
                          <li key={skillIndex} className="flex items-center space-x-2">
                            <Icon name="CheckCircle" size={14} className="text-accent flex-shrink-0" />
                            <span className="text-xs text-muted-foreground">{skill}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Connection Line */}
                {index < developmentPath.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-primary/30"></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <div className="bg-white rounded-2xl p-8 shadow-lg max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Ready to Start Your Journey?
            </h3>
            <p className="text-muted-foreground mb-6">
              Join Colombo Circle today and discover the leader within you through service, friendship, and meaningful experiences.
            </p>
            <button
              onClick={() => window.location.href = '/join'}
              className="bg-primary hover:bg-primary/90 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg flex items-center space-x-2 mx-auto"
            >
              <Icon name="UserPlus" size={20} />
              <span>Join Our Circle</span>
            </button>
          </div>
        </div>

        {/* Benefits Grid */}
        {/* <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div>
              <h3 className="text-3xl font-bold text-foreground mb-4">
                Why Choose Our Leadership Program?
              </h3>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Our comprehensive approach to leadership development combines practical experience with structured learning, ensuring you gain both the skills and confidence needed for professional success.
              </p>
            </div>
            
            <div className="space-y-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-md">
                  <h4 className="font-bold text-foreground mb-3">{benefit.category}</h4>
                  <div className="flex flex-wrap gap-2">
                    {benefit.items.map((item, itemIndex) => (
                      <span
                        key={itemIndex}
                        className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="relative">
            <Image
              src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
              alt="Leadership development in action"
              className="rounded-2xl w-full h-96 object-cover shadow-2xl"
            />
            <div className="absolute -bottom-6 -right-6 w-full h-full bg-secondary/20 rounded-2xl -z-10"></div> */}

        {/* Floating Stats */}
        {/* <div className="absolute -top-6 -left-6 bg-white rounded-xl p-4 shadow-lg">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">92%</div>
                <div className="text-xs text-muted-foreground">Career Growth</div>
              </div>
            </div>
            
            <div className="absolute bottom-6 right-6 bg-white rounded-xl p-4 shadow-lg">
              <div className="text-center">
                <div className="text-2xl font-bold text-secondary">15+</div>
                <div className="text-xs text-muted-foreground">Skills Gained</div>
              </div>
            </div>
          </div>
        </div> */}
      </div>
    </section>
  );
};

export default LeadershipDevelopmentSection;